/**
 * `window.__steelwake`: read-only diagnostics plus settings and practice hooks for the e2e tests
 * and the frame-time bench. Nothing here has gameplay authority (CLAUDE.md non-negotiable #1):
 * the practice hooks only exist for local practice matches and are refused by the runner in any
 * other mode. Version 4 (Phase 6) splits a base every rendering surface offers from the match
 * and hangar extensions, told apart by `mode`.
 */
import type { BotLevel, MatchEvent, MatchState } from '@steelwake/shared/combat';
import type { Loadout } from '@steelwake/shared/loadout';

import type { AudioStats } from '../audio/engine.js';
import type { NetConditions } from '../net/conditioner.js';
import type { NetMetrics } from '../net/metrics.js';
import type { EffectConfig, PresetName } from '../settings/presets.js';
import type { Settings } from '../settings/schema.js';
import { useSettingsStore } from '../settings/store.js';
import type { BackendName } from './backend.js';
import type { Game, MechSnapshot, VfxSnapshot } from './Game.js';
import type { MechLook } from './view/look.js';
import type { HangarHost, HangarSnapshot } from './hangar/HangarHost.js';
import type { MatchParams } from './match/setup.js';
import type { RenderSnapshot, RenderStatsSource } from './render/host.js';

export interface DebugApiBase {
  readonly version: 4;
  readonly mode: 'match' | 'hangar';
  readonly ready: boolean;
  readonly error: string | null;
  readonly backend: BackendName | null;
  readonly gpuTiming: boolean;
  readonly frameCount: number;
  stats(): RenderSnapshot;
  resetStats(): void;
  quality(): EffectConfig;
  setPreset(preset: PresetName): void;
  setSettings(partial: Partial<Settings>): void;
  getSettings(): Settings;
}

export interface MatchDebugApi extends DebugApiBase {
  readonly mode: 'match';
  mech(): MechSnapshot | null;
  /** Both seats' liveries and cosmetic parts as drawn. */
  looks(): [MechLook, MechLook] | null;
  /** The loadouts the match runs with, by seat. */
  loadouts(): [Loadout | null, Loadout | null];
  /** The published balance version the rules run under; 0 = the built-in data (Phase 7). */
  balanceVersion(): number;
  /** The full match state the HUD shows (a JSON snapshot; the server's truth online). */
  match(): MatchState | null;
  /** The state the own mech is drawn from (the prediction online; equals `match()` in practice). */
  predicted(): MatchState | null;
  /** Connection metrics for online matches; null in practice. */
  net(): NetMetrics | null;
  /** Re-tunes the simulated link of an online match (development and e2e only). */
  setNetConditions(conditions: NetConditions): void;
  lastEvents(): MatchEvent[];
  /** Practice parameters; null before the match resolves and for online matches. */
  params(): MatchParams | null;
  vfx(): VfxSnapshot;
  /** The audio engine (Phase 8): context state, buffers, cues played, voices, music. */
  audio(): AudioStats;
  setBot(level: BotLevel): void;
  setRoundSeconds(seconds: number): void;
  skipCountdown(): void;
  ultimateReady(): void;
  aimAtOpponent(): void;
  rematch(seed: number): void;
  /** Practice with `e2e=1` only: puts the own mech's feet at a point. */
  teleport(x: number, y: number, z: number): void;
  /** Starts a staged match (the loading screen's Deploy button). */
  deploy(): void;
  /** The kill cam (Phase 8): showing, and from which seat. */
  killcam(): { active: boolean; killer: number | null };
}

export interface HangarDebugApi extends DebugApiBase {
  readonly mode: 'hangar';
  hangar(): HangarSnapshot;
  selectMech(mechId: string, loadout: Loadout | null): Promise<void>;
  setLoadout(loadout: Loadout | null): void;
}

export type SteelwakeDebugApi = MatchDebugApi | HangarDebugApi;

declare global {
  interface Window {
    __steelwake?: SteelwakeDebugApi;
  }
}

let current: object | null = null;

type LiveKey = 'ready' | 'error' | 'backend' | 'gpuTiming' | 'frameCount';
const LIVE_KEYS: readonly LiveKey[] = ['ready', 'error', 'backend', 'gpuTiming', 'frameCount'];

/**
 * The shared part of the API. The live fields are defined as getters afterwards (`withLive`):
 * an object spread would read them once at install time and freeze the values.
 */
function base(source: RenderStatsSource): Omit<DebugApiBase, 'mode' | LiveKey> {
  return {
    version: 4,
    stats: () => source.snapshot(),
    resetStats: () => source.resetStats(),
    quality: () => source.quality(),
    setPreset: (preset) => useSettingsStore.getState().setPreset(preset),
    setSettings: (partial) => useSettingsStore.getState().update(partial),
    getSettings: () => useSettingsStore.getState().settings,
  };
}

function withLive<T extends object>(
  api: T,
  source: RenderStatsSource,
): T & Pick<DebugApiBase, LiveKey> {
  for (const key of LIVE_KEYS) {
    Object.defineProperty(api, key, { get: () => source[key], enumerable: true });
  }
  return api as T & Pick<DebugApiBase, LiveKey>;
}

export function installMatchDebugApi(game: Game): void {
  current = game;
  const api: MatchDebugApi = withLive<Omit<MatchDebugApi, LiveKey>>(
    {
      ...base(game),
      mode: 'match',
      mech: () => game.mechSnapshot(),
      looks: () => JSON.parse(JSON.stringify(game.looks())) as [MechLook, MechLook] | null,
      balanceVersion: () => game.balanceVersion(),
      loadouts: () =>
        JSON.parse(JSON.stringify(game.matchLoadouts())) as [Loadout | null, Loadout | null],
      match: () => {
        const state = game.matchState();
        return state ? (JSON.parse(JSON.stringify(state)) as MatchState) : null;
      },
      predicted: () => {
        const state = game.predictedState();
        return state ? (JSON.parse(JSON.stringify(state)) as MatchState) : null;
      },
      net: () => {
        const metrics = game.netMetrics();
        return metrics ? (JSON.parse(JSON.stringify(metrics)) as NetMetrics) : null;
      },
      setNetConditions: (conditions) => game.setNetConditions(conditions),
      lastEvents: () => JSON.parse(JSON.stringify(game.lastEvents())) as MatchEvent[],
      params: () => game.params,
      vfx: () => game.vfxSnapshot(),
      audio: () => game.audioSnapshot(),
      setBot: (level) => game.setBot(level),
      setRoundSeconds: (seconds) => game.setRoundSeconds(seconds),
      skipCountdown: () => game.skipCountdown(),
      ultimateReady: () => game.ultimateReady(),
      aimAtOpponent: () => game.aimAtOpponent(),
      rematch: (seed) => game.rematch(seed),
      teleport: (x, y, z) => game.teleport(x, y, z),
      deploy: () => game.deploy(),
      killcam: () => game.killcamSnapshot(),
    },
    game,
  );
  window.__steelwake = api;
}

export function installHangarDebugApi(host: HangarHost): void {
  current = host;
  const api: HangarDebugApi = withLive<Omit<HangarDebugApi, LiveKey>>(
    {
      ...base(host),
      mode: 'hangar',
      hangar: () => JSON.parse(JSON.stringify(host.hangar())) as HangarSnapshot,
      selectMech: (mechId, loadout) => host.selectMech(mechId, loadout),
      setLoadout: (loadout) => host.setLoadout(loadout),
    },
    host,
  );
  window.__steelwake = api;
}

export function removeDebugApi(owner: object): void {
  if (current === owner) {
    current = null;
    delete window.__steelwake;
  }
}
